import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Lock, CheckCircle, AlertCircle, Loader2, Eye, EyeOff, ShieldX, Clock, UserX } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

// Error type mapping for user-friendly messages
const ERROR_STATES = {
  invalid: {
    icon: ShieldX,
    title: "Invalid Invitation",
    message: "This invitation link is not valid. Please check the link or request a new invitation.",
    color: "text-red-400"
  },
  expired: {
    icon: Clock,
    title: "Invitation Expired", 
    message: "This invitation has expired. Please contact your Platform Owner for a new invitation link.",
    color: "text-amber-400"
  },
  revoked: {
    icon: UserX,
    title: "Invitation Revoked",
    message: "This invitation has been revoked by the Platform Owner.",
    color: "text-red-400"
  },
  used: {
    icon: CheckCircle,
    title: "Already Accepted",
    message: "This invitation has already been used to create an account. Please log in instead.",
    color: "text-blue-400"
  }
};

export default function PlatformInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [verifying, setVerifying] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [formError, setFormError] = useState(null);
  
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Password validation rules
  const passwordRules = {
    minLength: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };
  const passwordValid = Object.values(passwordRules).every(Boolean);

  // Verify the invite token on mount
  useEffect(() => {
    const verifyInvite = async () => {
      if (!token) {
        setErrorType("invalid");
        setVerifying(false);
        return;
      }

      try {
        const { data } = await api.get(`/platform/invite/verify/${token}`);
        setInviteData(data);
        setFullName(data.full_name || "");
      } catch (err) {
        const detail = err.response?.data?.detail?.toLowerCase() || "";
        
        // Map error messages to error types - order matters!
        if (detail.includes("already been accepted") || detail.includes("already used")) {
          setErrorType("used");
        } else if (detail.includes("revoked")) {
          setErrorType("revoked");
        } else if (detail.includes("invalid") && !detail.includes("expired")) {
          // Pure "invalid" message (not "invalid or expired")
          setErrorType("invalid");
        } else if (detail.includes("expired") && !detail.includes("invalid")) {
          // Pure "expired" message
          setErrorType("expired");
        } else {
          setErrorType("invalid");
        }
      } finally {
        setVerifying(false);
      }
    };
    
    verifyInvite();
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    setFormError(null);
    
    // Client-side validation
    if (!fullName.trim()) {
      setFormError("Please enter your full name");
      return;
    }
    
    if (password !== confirmPassword) {
      setFormError("Passwords do not match");
      return;
    }
    
    if (!passwordValid) {
      setFormError("Password does not meet requirements");
      return;
    }
    
    setAccepting(true);
    
    try {
      await api.post("/platform/invite/accept", {
        token,
        full_name: fullName.trim(),
        password
      });
      
      setSuccess(true);
      
      // Redirect to admin login after short delay
      setTimeout(() => {
        navigate("/admin", { state: { message: "Account activated successfully. Please log in." } });
      }, 2500);
      
    } catch (err) {
      const detail = err.response?.data?.detail?.toLowerCase() || "";
      
      // Handle specific backend errors
      if (detail.includes("expired")) {
        setErrorType("expired");
      } else if (detail.includes("already") || detail.includes("no longer valid")) {
        setErrorType("used");
      } else if (detail.includes("password")) {
        setFormError(err.response?.data?.detail || "Password does not meet requirements");
      } else {
        setFormError("Failed to activate account. Please try again.");
      }
    } finally {
      setAccepting(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#131E1E] flex items-center justify-center" data-testid="invite-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#7EBDBC] animate-spin mx-auto mb-4" />
          <p className="text-white/50 text-sm">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  // Error state (invalid/expired/revoked/used invite)
  if (errorType) {
    const errorConfig = ERROR_STATES[errorType] || ERROR_STATES.invalid;
    const ErrorIcon = errorConfig.icon;
    
    return (
      <div className="min-h-screen bg-[#131E1E] flex items-center justify-center px-4" data-testid="invite-error">
        <div className="w-full max-w-sm text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            errorType === "used" ? "bg-blue-500/10" : "bg-red-500/10"
          }`}>
            <ErrorIcon className={`w-8 h-8 ${errorConfig.color}`} />
          </div>
          <h1 className="text-xl font-medium text-white mb-2" style={H} data-testid="invite-error-title">
            {errorConfig.title}
          </h1>
          <p className="text-white/50 text-sm mb-6" data-testid="invite-error-message">
            {errorConfig.message}
          </p>
          {errorType === "used" && (
            <button
              onClick={() => navigate("/admin")}
              className="px-6 py-2.5 bg-[#205B5A] text-white rounded-full text-sm font-medium hover:bg-[#174544] transition-colors"
              data-testid="goto-login-btn"
            >
              Go to Login
            </button>
          )}
          {errorType !== "used" && (
            <p className="text-white/30 text-xs">
              Contact your Platform Owner for assistance.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#131E1E] flex items-center justify-center px-4" data-testid="invite-success">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-[#205B5A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-[#7EBDBC]" />
          </div>
          <h1 className="text-xl font-medium text-white mb-2" style={H}>Account Activated</h1>
          <p className="text-white/50 text-sm mb-4">
            Welcome to the LumeroFlow platform team!
          </p>
          <p className="text-white/30 text-xs">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Accept invitation form
  return (
    <div className="min-h-screen bg-[#131E1E] flex items-center justify-center px-4" data-testid="invite-form">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-[#205B5A]/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Lock className="w-5 h-5 text-[#7EBDBC]" />
          </div>
          <h1 className="text-xl font-medium text-white" style={H}>Join LumeroFlow Platform</h1>
          <p className="text-xs text-white/50 mt-1">Complete your account setup</p>
        </div>
        
        {/* Invite Details */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6" data-testid="invite-details">
          <p className="text-[10px] text-white/40 uppercase tracking-wider mb-2">Invitation for</p>
          <p className="text-sm text-white font-medium" data-testid="invite-email">{inviteData?.email}</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-[9px] font-semibold px-2 py-0.5 bg-[#205B5A]/20 text-[#7EBDBC] rounded-full uppercase" data-testid="invite-role">
              {inviteData?.platform_role?.replace(/_/g, " ")}
            </span>
          </div>
          {inviteData?.expires_at && (
            <p className="text-[10px] text-white/30 mt-2">
              Expires: {new Date(inviteData.expires_at).toLocaleDateString("en-GB", { 
                day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" 
              })}
            </p>
          )}
        </div>
        
        <form onSubmit={handleAccept} className="space-y-4" data-testid="invite-accept-form">
          {formError && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-300 text-sm px-4 py-3 rounded-xl flex items-center gap-2" data-testid="form-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {formError}
            </div>
          )}
          
          <div>
            <label className="block text-xs text-white/50 mb-1">Full Name *</label>
            <input 
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              required
              placeholder="Your full name"
              disabled={accepting}
              data-testid="fullname-input"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#7EBDBC] disabled:opacity-50"
            />
          </div>
          
          <div>
            <label className="block text-xs text-white/50 mb-1">Create Password *</label>
            <div className="relative">
              <input 
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder="Create a strong password"
                disabled={accepting}
                data-testid="password-input"
                className="w-full px-4 py-3 pr-10 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#7EBDBC] disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Password requirements */}
            {password && (
              <div className="mt-2 grid grid-cols-2 gap-1" data-testid="password-requirements">
                <div className={`text-[10px] flex items-center gap-1 ${passwordRules.minLength ? 'text-[#7EBDBC]' : 'text-white/30'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${passwordRules.minLength ? 'bg-[#7EBDBC]' : 'bg-white/20'}`} />
                  8+ characters
                </div>
                <div className={`text-[10px] flex items-center gap-1 ${passwordRules.hasUpper ? 'text-[#7EBDBC]' : 'text-white/30'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${passwordRules.hasUpper ? 'bg-[#7EBDBC]' : 'bg-white/20'}`} />
                  Uppercase letter
                </div>
                <div className={`text-[10px] flex items-center gap-1 ${passwordRules.hasLower ? 'text-[#7EBDBC]' : 'text-white/30'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${passwordRules.hasLower ? 'bg-[#7EBDBC]' : 'bg-white/20'}`} />
                  Lowercase letter
                </div>
                <div className={`text-[10px] flex items-center gap-1 ${passwordRules.hasNumber ? 'text-[#7EBDBC]' : 'text-white/30'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${passwordRules.hasNumber ? 'bg-[#7EBDBC]' : 'bg-white/20'}`} />
                  Number
                </div>
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-xs text-white/50 mb-1">Confirm Password *</label>
            <div className="relative">
              <input 
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your password"
                disabled={accepting}
                data-testid="confirm-password-input"
                className={`w-full px-4 py-3 pr-10 rounded-xl bg-white/5 border text-white text-sm placeholder-white/30 focus:outline-none disabled:opacity-50 ${
                  confirmPassword && confirmPassword !== password 
                    ? 'border-red-500/50 focus:border-red-500' 
                    : 'border-white/10 focus:border-[#7EBDBC]'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {confirmPassword && confirmPassword !== password && (
              <p className="text-[10px] text-red-400 mt-1">Passwords do not match</p>
            )}
          </div>
          
          <p className="text-[10px] text-white/30">
            By activating your account, you agree to follow platform security policies and maintain confidentiality of operational data.
          </p>
          
          <button 
            type="submit" 
            disabled={accepting || !passwordValid || password !== confirmPassword || !fullName.trim()}
            data-testid="activate-btn"
            className="w-full bg-[#205B5A] text-white py-3 rounded-full font-medium text-sm hover:bg-[#174544] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {accepting && <Loader2 className="w-4 h-4 animate-spin" />}
            {accepting ? "Activating Account..." : "Activate Account"}
          </button>
        </form>
        
        <p className="text-center text-[10px] text-white/20 mt-6">
          Already have an account?{" "}
          <button 
            onClick={() => navigate("/admin")} 
            className="text-[#7EBDBC] hover:underline"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
